import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usersAPI, toolsAPI, rechtenAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Save, Loader2, Info } from 'lucide-react';

export default function RechtenTab() {
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTool, setSelectedTool] = useState('');
  const [rechtenData, setRechtenData] = useState({});
  const [rolChanged, setRolChanged] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pendingRol, setPendingRol] = useState(null); // { userId, userNaam, oldRol, newRol }
  const scrollRef = useRef(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [toolsRes, usersRes] = await Promise.all([toolsAPI.list(), usersAPI.list()]);
        if (toolsRes.ok) {
          const t = await toolsRes.clone().json();
          setTools(t);
          if (t.length > 0) setSelectedTool(t[0].tool_id);
        }
        if (usersRes.ok) setUsers(await usersRes.clone().json());
      } catch {} finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadRechten = useCallback(async () => {
    if (!selectedTool) return;
    try {
      const res = await rechtenAPI.getForTool(selectedTool);
      if (res.ok) {
        const data = await res.clone().json();
        const map = {};
        data.forEach(r => { map[r.user_id] = r; });
        setRechtenData(map);
      }
    } catch {}
    setRolChanged({});
    setDirty(false);
  }, [selectedTool]);

  useEffect(() => { loadRechten(); }, [loadRechten]);

  // Scroll shadow detection
  const checkScrollShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftShadow(el.scrollLeft > 0);
    setShowRightShadow(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScrollShadows();
    el.addEventListener('scroll', checkScrollShadows);
    const observer = new ResizeObserver(checkScrollShadows);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScrollShadows);
      observer.disconnect();
    };
  }, [checkScrollShadows, users, selectedTool]);

  const currentTool = tools.find(t => t.tool_id === selectedTool);
  const pages = currentTool?.beschikbare_paginas || [];
  const beschikbareRollen = currentTool?.beschikbare_rollen || [];
  const heeftRollen = beschikbareRollen.length > 0;

  // Lookup helpers for role objects
  const findRol = (naam) => beschikbareRollen.find(r => r.naam === naam) || null;
  const labelFor = (naam) => findRol(naam)?.label || naam || '';

  const getUserPageAccess = (userId, page) => {
    return rechtenData[userId]?.paginas?.[page] || false;
  };

  const getUserToolAccess = (userId) => {
    return rechtenData[userId]?.toegang || false;
  };

  const getUserRol = (userId) => {
    return rechtenData[userId]?.tool_rol || '';
  };

  const requestRolChange = (userId, userNaam, newRol) => {
    const oldRol = getUserRol(userId);
    // Treat the special "__none__" sentinel as clearing the role
    const normalizedNew = newRol === '__none__' ? '' : newRol;
    if (normalizedNew === oldRol) return; // no-op
    const newRolObj = normalizedNew ? findRol(normalizedNew) : null;
    const hasDefaults = !!newRolObj && Object.keys(newRolObj.default_paginas || {}).length > 0;
    setPendingRol({
      userId,
      userNaam,
      oldRol,
      newRol: normalizedNew,
      newRolHasDefaults: hasDefaults,
    });
  };

  // applyDefaults: when true, overwrite paginas with role's default_paginas
  const applyRolChange = (applyDefaults) => {
    if (!pendingRol) return;
    const { userId, newRol } = pendingRol;
    const newRolObj = newRol ? findRol(newRol) : null;
    setRechtenData(prev => {
      const current = { ...prev };
      if (!current[userId]) {
        current[userId] = {
          user_id: userId,
          tool_id: selectedTool,
          toegang: applyDefaults ? true : false,
          paginas: {},
        };
        pages.forEach(p => { current[userId].paginas[p] = false; });
      }

      let nextPaginas = current[userId].paginas;
      let nextToegang = current[userId].toegang;

      if (applyDefaults && newRolObj) {
        // Build paginas from defaults: every tool pagina gets a value from the
        // role's default_paginas (default false when not specified).
        const defaults = newRolObj.default_paginas || {};
        const built = {};
        pages.forEach(p => { built[p] = !!defaults[p]; });
        nextPaginas = built;
        // Auto-grant toegang when applying defaults — admin intent is "apply this role"
        nextToegang = true;
      }

      current[userId] = {
        ...current[userId],
        tool_rol: newRol || null,
        paginas: nextPaginas,
        toegang: nextToegang,
      };
      return current;
    });
    setRolChanged(prev => ({ ...prev, [userId]: true }));
    setDirty(true);
    setPendingRol(null);
  };

  const togglePageAccess = (userId, page) => {
    setDirty(true);
    setRechtenData(prev => {
      const current = { ...prev };
      if (!current[userId]) {
        current[userId] = { user_id: userId, tool_id: selectedTool, toegang: true, paginas: {} };
        pages.forEach(p => { current[userId].paginas[p] = false; });
      }
      current[userId] = {
        ...current[userId],
        paginas: { ...current[userId].paginas, [page]: !current[userId].paginas?.[page] },
      };
      return current;
    });
  };

  const toggleToolAccess = (userId) => {
    setDirty(true);
    setRechtenData(prev => {
      const current = { ...prev };
      if (!current[userId]) {
        current[userId] = { user_id: userId, tool_id: selectedTool, toegang: false, paginas: {} };
        pages.forEach(p => { current[userId].paginas[p] = false; });
      }
      current[userId] = { ...current[userId], toegang: !current[userId].toegang };
      return current;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const userId of Object.keys(rechtenData)) {
        const r = rechtenData[userId];
        const paginas = {};
        pages.forEach(p => { paginas[p] = r.paginas?.[p] || false; });
        const body = { toegang: r.toegang || false, paginas };
        // Only include tool_rol when explicitly changed during this session,
        // so existing assignments stay untouched (backend uses exclude_unset).
        if (rolChanged[userId]) {
          body.tool_rol = r.tool_rol || null;
        }
        await rechtenAPI.update(userId, selectedTool, body);
      }
      toast.success('Rechten opgeslagen');
      setRolChanged({});
      setDirty(false);
    } catch { toast.error('Fout bij opslaan'); }
    setSaving(false);
  };

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  if (tools.length === 0) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-slate-500">Geen tools beschikbaar</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-center gap-3">
          <Select value={selectedTool} onValueChange={v => setSelectedTool(v)}>
            <SelectTrigger className="w-[220px] h-9" data-testid="rights-tool-select">
              <SelectValue placeholder="Selecteer tool" />
            </SelectTrigger>
            <SelectContent>
              {tools.map(t => <SelectItem key={t.tool_id} value={t.tool_id}>{t.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {dirty && <Badge className="bg-amber-50 text-amber-700 border-amber-100">Niet opgeslagen wijzigingen</Badge>}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
          data-testid="rights-save-button"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Opslaan
        </Button>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
        <Info className="w-3.5 h-3.5" />
        <span>Admin-gebruikers en hoger hebben altijd volledige toegang.</span>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0 relative">
          {/* Left scroll shadow */}
          {showLeftShadow && (
            <div className="absolute top-0 left-[180px] bottom-0 w-6 bg-gradient-to-r from-black/[0.06] to-transparent z-10 pointer-events-none" />
          )}
          {/* Right scroll shadow */}
          {showRightShadow && (
            <div className="absolute top-0 right-0 bottom-0 w-6 bg-gradient-to-l from-black/[0.06] to-transparent z-10 pointer-events-none" />
          )}

          <div ref={scrollRef} className="overflow-x-auto" data-testid="rights-matrix">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 sticky left-0 bg-white z-20 min-w-[180px] max-w-[180px] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-200">
                    Gebruiker
                  </th>
                  <th className="p-3 text-xs font-semibold text-slate-600 text-center min-w-[70px]">Toegang</th>
                  {heeftRollen && (
                    <th className="p-3 text-xs font-semibold text-slate-600 text-center min-w-[140px] whitespace-nowrap" data-testid="rights-rol-header">
                      Rol
                    </th>
                  )}
                  {pages.map(p => (
                    <th key={p} className="p-3 text-xs font-semibold text-slate-600 text-center min-w-[90px] whitespace-nowrap">
                      <span className="capitalize">{p}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="p-3 sticky left-0 bg-white z-20 min-w-[180px] max-w-[180px] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-slate-100">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                          style={{ backgroundColor: u.avatar_kleur || '#1a2332' }}
                        >
                          {u.naam?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-[#1a2332] truncate">{u.naam}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Checkbox
                        checked={getUserToolAccess(u.id)}
                        onCheckedChange={() => toggleToolAccess(u.id)}
                        data-testid="rights-cell-checkbox"
                      />
                    </td>
                    {heeftRollen && (
                      <td className="p-3 text-center" data-testid={`rights-rol-cell-${u.id}`}>
                        <Select
                          value={getUserRol(u.id) || '__none__'}
                          onValueChange={(v) => requestRolChange(u.id, u.naam, v)}
                          disabled={!getUserToolAccess(u.id)}
                        >
                          <SelectTrigger
                            className="h-8 text-xs"
                            data-testid={`rights-rol-trigger-${u.id}`}
                          >
                            <SelectValue placeholder="Geen rol" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" data-testid={`rights-rol-option-none-${u.id}`}>Geen rol</SelectItem>
                            {beschikbareRollen.map(r => (
                              <SelectItem key={r.naam} value={r.naam} data-testid={`rights-rol-option-${r.naam}-${u.id}`}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    {pages.map(p => (
                      <td key={p} className="p-3 text-center">
                        <Checkbox
                          checked={getUserPageAccess(u.id, p)}
                          onCheckedChange={() => togglePageAccess(u.id, p)}
                          disabled={!getUserToolAccess(u.id)}
                          data-testid="rights-cell-checkbox"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirm role change dialog — three-way: cancel / apply with defaults / apply only role */}
      {pendingRol && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPendingRol(null)}
          data-testid="rights-rol-confirm-dialog"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 pb-4">
              <h3 className="text-lg font-semibold text-[#1a2332]">Rol wijzigen</h3>
              <p className="text-sm text-slate-600 mt-2">
                Rol wijzigen van{' '}
                {pendingRol.oldRol
                  ? <span className="font-semibold">{labelFor(pendingRol.oldRol)}</span>
                  : <span className="italic text-slate-500">geen rol</span>}
                {' '}naar{' '}
                {pendingRol.newRol
                  ? <span className="font-semibold">{labelFor(pendingRol.newRol)}</span>
                  : <span className="italic text-slate-500">geen rol</span>}
                {' '}voor <span className="font-semibold">{pendingRol.userNaam}</span>.
              </p>
              {pendingRol.newRolHasDefaults ? (
                <p className="text-sm text-slate-600 mt-3">
                  Wil je de pagina-rechten herstellen naar de standaard voor{' '}
                  &quot;{labelFor(pendingRol.newRol)}&quot;?
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-3 italic">
                  {pendingRol.newRol
                    ? 'Deze rol heeft geen standaard pagina-rechten ingesteld — alleen de rol-toewijzing wijzigt.'
                    : 'Alleen de rol-toewijzing wijzigt; pagina-rechten blijven ongewijzigd.'}
                </p>
              )}
            </div>
            <div className="px-6 pb-6 flex flex-col gap-2">
              {pendingRol.newRolHasDefaults && (
                <Button
                  type="button"
                  onClick={() => applyRolChange(true)}
                  className="w-full bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press"
                  data-testid="rights-rol-confirm-with-defaults"
                >
                  Ja, paginas herstellen
                </Button>
              )}
              <Button
                type="button"
                variant={pendingRol.newRolHasDefaults ? 'outline' : 'default'}
                onClick={() => applyRolChange(false)}
                className={pendingRol.newRolHasDefaults
                  ? 'w-full rounded-xl'
                  : 'w-full bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press'}
                data-testid="rights-rol-confirm-only-role"
              >
                {pendingRol.newRolHasDefaults ? 'Nee, alleen rol wijzigen' : 'Bevestigen'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPendingRol(null)}
                className="w-full rounded-xl text-slate-500 hover:text-slate-700"
                data-testid="rights-rol-confirm-cancel"
              >
                Annuleren
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
